/**
 * Technician Passport targets for a shop's roster rows (Shop Profile, Technicians tab).
 *
 * Oct 20 audit A1: "View Tech Passport" used to navigate to `/passport` — the
 * VIEWER's own Passport — for any roster row bound to a user but lacking
 * `technician_profile_id`. This resolves every row's target up front, in two
 * batch reads (never one per row), and through the same rule as Team
 * Management (`resolveRosterTechnicianPassport`):
 *
 *   - the viewer's own row → `/passport`;
 *   - otherwise the person's OWN technician profile, only if the server
 *     says this viewer may open it;
 *   - otherwise unavailable, with a reason. Nothing is created.
 *
 * Read-only. `technician_users` is read under the viewer's own RLS and the
 * visible profiles come from `visible_technician_passports` (the caller's own
 * audience, server-decided); no service role, no writes.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveRosterTechnicianPassport } from '@/lib/passport/teamPassportViews';

export interface RosterTechnicianPassportTarget {
  href: string | null;
  unavailableReason: string | null;
}

export function useRosterTechnicianPassports(
  shopId: string | null | undefined,
  techs: readonly { id: string; technician_profile_id?: string | null }[],
  viewerUserId: string | null | undefined,
) {
  const techIds = techs.map((t) => t.id).sort();
  return useQuery({
    queryKey: ['roster-technician-passports', shopId, viewerUserId, techIds.join(','),
      techs.map((t) => t.technician_profile_id ?? '').join(',')],
    enabled: !!shopId && techs.length > 0,
    queryFn: async (): Promise<Map<string, RosterTechnicianPassportTarget>> => {
      const { data: links } = await supabase
        .from('technician_users')
        .select('technician_id, user_id')
        .in('technician_id', techIds);
      const userByTech = new Map<string, string>();
      for (const l of (links ?? []) as { technician_id: string; user_id: string | null }[]) {
        if (l.user_id) userByTech.set(l.technician_id, l.user_id);
      }

      const rosterProfileIds = techs.map((t) => t.technician_profile_id).filter((x): x is string => !!x);
      const linkedUserIds = [...new Set(userByTech.values())];
      // Oct 20 addendum P1/P2: the server answers which Technician Passports this viewer may open (self,
      // Owner/Manager, same-shop Service Advisor, ORG_ADMIN of the shop's organisation, recruiting). Ids only.
      const { data: visible } = rosterProfileIds.length || linkedUserIds.length
        ? await supabase.rpc('visible_technician_passports' as never, {
          p_user_ids: linkedUserIds.length ? linkedUserIds : null,
          p_profile_ids: rosterProfileIds.length ? rosterProfileIds : null,
        } as never)
        : { data: [] };
      const visibleProfileIds = new Set<string>();
      const profileByUser = new Map<string, string>();
      for (const p of [...((visible ?? []) as unknown as { technician_profile_id: string; user_id: string | null }[])]
        .sort((a, b) => a.technician_profile_id.localeCompare(b.technician_profile_id))) {
        visibleProfileIds.add(p.technician_profile_id);
        if (p.user_id && !profileByUser.has(p.user_id)) profileByUser.set(p.user_id, p.technician_profile_id);
      }

      const out = new Map<string, RosterTechnicianPassportTarget>();
      for (const t of techs) {
        const linkedUserId = userByTech.get(t.id) ?? null;
        out.set(t.id, resolveRosterTechnicianPassport({
          rosterProfileId: t.technician_profile_id ?? null,
          linkedUserId,
          visibleProfileIds,
          visibleProfileIdForLinkedUser: linkedUserId ? profileByUser.get(linkedUserId) ?? null : null,
          viewerUserId,
        }));
      }
      return out;
    },
  });
}
